import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { BellRing, Loader2, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { cn } from "@/lib/utils";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import {
  fetchCompanyDocumentAcknowledgements,
  remindCompanyDocumentAcknowledgements,
} from "@/services/companyDocumentService";
import { SegmentedControl } from "./CompanyDocumentsList";

const formatDateTime = (value) => (value ? format(new Date(value), "dd MMM yyyy, h:mm a") : "");

const STATUS_STYLES = {
  acknowledged: {
    label: "Acknowledged",
    className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  },
  opened: {
    label: "Opened",
    className: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  },
  not_opened: {
    label: "Not opened",
    className: "bg-muted text-muted-foreground",
  },
};

const whenOf = (employee) => {
  if (employee.status === "acknowledged") return formatDateTime(employee.acknowledged_at);
  if (employee.status === "opened") return formatDateTime(employee.opened_at);
  return "";
};

/** Who has and hasn't acknowledged a document's current file (admin). */
export default function CompanyDocumentAcknowledgementsDialog({ doc, canRemind, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmingReminder, setConfirmingReminder] = useState(false);
  const [reminding, setReminding] = useState(false);

  const docId = doc?._id;

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchCompanyDocumentAcknowledgements(docId);
      setEmployees(data?.employees || []);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        setLoadError(getErrorMessage(error, "Failed to load acknowledgements"));
      }
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    setFilter("all");
    setSearchTerm("");
    setEmployees([]);
    load();
  }, [load]);

  const outstanding = useMemo(
    () => employees.filter((e) => e.status !== "acknowledged").length,
    [employees],
  );

  const visible = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return employees.filter((e) => {
      if (filter === "acknowledged" && e.status !== "acknowledged") return false;
      if (filter === "outstanding" && e.status === "acknowledged") return false;
      if (!q) return true;
      return [e.name, e.employee_code, e.department]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [employees, filter, searchTerm]);

  const handleRemind = async () => {
    setReminding(true);
    try {
      const { sent } = await remindCompanyDocumentAcknowledgements(docId);
      if (sent) {
        toast.success(`Reminder sent to ${sent} employee${sent === 1 ? "" : "s"}`);
      } else {
        toast.info("Nobody needs a reminder");
      }
    } catch (error) {
      if (!isAuthExpiryError(error)) toast.error(getErrorMessage(error, "Failed to send reminders"));
    } finally {
      setReminding(false);
    }
  };

  const archived = Boolean(doc?.archived_at);
  const showRemind = canRemind && !archived && !loading && !loadError && outstanding > 0;

  return (
    <>
      <Dialog open={Boolean(doc)} onOpenChange={(next) => !next && onClose()}>
        {/* Header and actions stay put; only the list scrolls. */}
        <DialogContent className="flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[560px]">
          <DialogHeader className="shrink-0 px-5 pb-3 pt-5 pr-12 text-left sm:px-6 sm:pt-6">
            <DialogTitle>Acknowledgements</DialogTitle>
            <DialogDescription className="break-words">
              {doc?.title} · {doc?.file_name}
            </DialogDescription>
          </DialogHeader>

          <div className="shrink-0 space-y-3 px-5 pb-3 sm:px-6">
            <SegmentedControl
              ariaLabel="Filter by acknowledgement"
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: `All (${employees.length})` },
                { value: "acknowledged", label: `Done (${employees.length - outstanding})` },
                { value: "outstanding", label: `Pending (${outstanding})` },
              ]}
            />
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                aria-label="Search employees"
              />
            </div>
          </div>

          <div className="min-h-[12rem] flex-1 overflow-y-auto border-t border-border px-5 sm:px-6">
            {loading ? (
              <div className="space-y-3 py-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center py-10 text-center">
                <p className="text-sm text-muted-foreground">{loadError}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={load}>
                  <RefreshCw className="h-4 w-4" />
                  Try again
                </Button>
              </div>
            ) : visible.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                {employees.length === 0 ? "Nobody is asked to acknowledge this document." : "No matching employees."}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {visible.map((employee) => {
                  const style = STATUS_STYLES[employee.status] || STATUS_STYLES.not_opened;
                  const when = whenOf(employee);
                  return (
                    <li key={employee._id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground" title={employee.name}>
                          {employee.name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[employee.employee_code, employee.department].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <span
                          className={cn(
                            "inline-flex rounded-md px-2 py-0.5 text-xs font-medium",
                            style.className,
                          )}
                        >
                          {style.label}
                        </span>
                        {when && <p className="mt-0.5 text-[11px] text-muted-foreground">{when}</p>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <DialogFooter className="shrink-0 flex-row gap-2 space-x-0 border-t border-border px-5 py-3 sm:space-x-0 sm:px-6">
            <Button variant="outline" className="flex-1 sm:flex-none" onClick={onClose}>
              Close
            </Button>
            {showRemind && (
              <Button
                className="flex-1 sm:flex-none"
                onClick={() => setConfirmingReminder(true)}
                disabled={reminding}
              >
                {reminding ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <BellRing className="h-4 w-4" />
                )}
                Send reminder
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmingReminder}
        onOpenChange={setConfirmingReminder}
        title="Send Reminder?"
        description={`${outstanding} employee${outstanding === 1 ? "" : "s"} who haven't acknowledged this document will get a notification.`}
        message={doc ? `"${doc.title}"` : ""}
        confirmText="Send Reminder"
        onConfirm={handleRemind}
      />
    </>
  );
}
